/**
 * Expedition: what a run is made of.
 *
 * The rules that decide a room -- what lives in it, what it asks of you, how
 * long it gives you and which doors come next -- with no DOM and no renderer
 * anywhere near them. They were in `main.ts` while the mode was one room type
 * and one queue, and moving them out is what lets `expedition-test` check the
 * shape of a run instead of only the simulation inside one.
 *
 * The run *state* stays in `main.ts`, because saving, resuming and entering a
 * room are all bound up with the page.
 */
import { ARENAS } from './maps';
import { EnemyKind, RoomGoal } from './types';
import type { EnemyKindValue, EnemySpawn, MatchSettings, RoomGoalValue } from './types';

/**
 * Expedition: a run of rooms rather than a ladder of matches.
 *
 * The campaign above is nine independent matches that get harder. This is one
 * continuous attempt: the range and bombs you pick up in room two are still
 * yours in room six, and dying ends the whole thing rather than the stage.
 * That difference is the entire mode -- it is what makes a power-up a decision
 * about a run instead of a consumable that expires in ninety seconds.
 *
 * Rooms are rolled from the arenas that already exist rather than authored,
 * which is deliberate: both times this genre has been attempted with hand-built
 * worlds -- Bomberman Quest, Bomberman Tournament -- the exploration was judged
 * the thin half and the bomber combat the good half. So the combat is the
 * content, and the structure is what makes it a run.
 *
 * What fills a room is creatures, not bombers. The first cut of this mode used
 * bots, because bots were the only opponent the engine had -- which made a run
 * eight rounds of deathmatch with a save file, and put four opponents on the
 * board who detour for the same power-ups the whole mode is built on carrying.
 * A creature cannot place a bomb and cannot pick anything up. See `EnemyState`.
 */
export interface RoomDef {
  map: string;
  /** What lives in it. See `EnemyKind`. */
  enemies: EnemySpawn[];
  /** What it asks of you. See `RoomGoal`. */
  goal: RoomGoalValue;
  label: string;
}

/**
 * Is this something `enterExpeditionRoom` could actually start?
 *
 * Every field checked here is dereferenced later: the map is looked up,
 * `GOAL_TEXT[goal]` is indexed for the banner, `roomSeconds` switches on the
 * goal and `ENEMY_SPECIES[kind]` is indexed on the room's first tick.
 */
export function playableRoom(r: RoomDef | undefined): boolean {
  if (!r || typeof r.label !== 'string') return false;
  if (typeof r.map !== 'string' || !ARENAS.some((a) => a.id === r.map)) return false;
  if (!KNOWN_ROOM_GOALS.has(r.goal as string)) return false;
  if (!Array.isArray(r.enemies) || !r.enemies.length) return false;
  let total = 0;
  for (const spawn of r.enemies) {
    if (!spawn || !KNOWN_ENEMY_KINDS.has(spawn.kind as number)) return false;
    if (!Number.isInteger(spawn.count) || spawn.count < 0) return false;
    total += spawn.count;
  }
  return total >= 1 && total <= EXPEDITION_MAX_ENEMIES;
}

/** How many rooms a run is. Sized for the 5-15 minutes a run is meant to take. */
export const EXPEDITION_ROOMS = 8;

/** The most creatures a room may hold, and what `loadExpedition` validates against. */
export const EXPEDITION_MAX_ENEMIES = 12;

/** Derived from the enum, so adding a species cannot leave the validator behind. */
export const KNOWN_ENEMY_KINDS = new Set<number>(Object.values(EnemyKind));

/** Likewise for objectives. A run saved before a goal existed is discarded. */
export const KNOWN_ROOM_GOALS = new Set<string>(Object.values(RoomGoal));

/**
 * The rules a run is played under, pinned rather than inherited.
 *
 * `buildSettings` fills anything a caller does not name from `prefs`, which is
 * right for Quick Match -- the setup panel is where those choices live -- and
 * wrong here twice over. A run would be played under whatever was last set for
 * an unrelated mode, and `bestExpedition` would then rank a run at one hit
 * point against a run at three and call the larger number the better one.
 *
 * These are today's defaults, which is what the mode was built and driven
 * against. The point is not that they are new, it is that they stop moving.
 * Passed as `base` rather than as overrides so `?hp=1` and the rest of the
 * trial flags can still sweep a run; the rules that *define* the mode are
 * pinned at the call site instead, where nothing can reach them.
 */
export const EXPEDITION_RULES: Partial<MatchSettings> = {
  /*
   * Several of these are inert in a room with one player and no bots --
   * `resolve` scales by how far behind you are, `catchup` bends a drop table
   * toward whoever is losing, `friendlyFire` needs somebody to shoot, and
   * `loseGearOnDeath` cannot fire when death ends the run. They stay pinned
   * anyway. Dropping an inert rule from this list does not remove it, it hands
   * it back to `prefs` -- so the day a room gains a rival bomber, a run's
   * rules would quietly start moving with a setup panel again, which is the
   * exact bug this object exists to close.
   */
  hitPoints: 3,
  resolve: true,
  loseGearOnDeath: true,
  catchup: true,
  arenaEvents: false,
  deflect: true,
  suddenDeath: true,
  friendlyFire: false,
  itemDensity: 1,
};

/**
 * What lives in room `i` of `n`, as a curve rather than a table.
 *
 * The three species arrive one at a time rather than all at once, because each
 * one is a lesson and a room teaching two at a time teaches neither. Drifters
 * from the start -- they are the one you learn to read. Stalkers from the
 * quarter mark, which is where a player who has learned to stand still finds
 * out that standing still is now a choice. Phasers from halfway, because a
 * creature that walks through walls is only interesting once walls mean
 * something to you.
 *
 * Count climbs with depth and so does the mix, so difficulty comes from the
 * board getting busier rather than from taking the player's gear away -- which
 * is the one thing this mode must never do.
 */
export function roomEnemies(i: number, rooms: number): EnemySpawn[] {
  /*
   * The last room is the brooder's, and it is not a bigger version of the room
   * before it. One boss and a thin escort: the escort exists so the opening
   * seconds are not just walking toward it, and it is deliberately small
   * because the brood the boss leaves is where the room gets crowded.
   */
  if (i === rooms - 1) {
    return [
      { kind: EnemyKind.BROODER, count: 1 },
      { kind: EnemyKind.DRIFTER, count: 3 },
      { kind: EnemyKind.STALKER, count: 1 },
    ];
  }
  /*
   * One more creature than the room before it, every room, so no two rooms in
   * a run are the same size. A ramp that rounds into pairs -- two easy, two
   * medium -- gives eight rooms four difficulties, and the second of each pair
   * is the one that feels like filler.
   */
  const total = Math.min(EXPEDITION_MAX_ENEMIES, 3 + i);
  const t = i / Math.max(1, rooms - 1);

  // The harder species are a share of that total rather than an addition to
  // it, so the ramp stays where it was set and the *mix* is what hardens.
  const stalkers = t >= 0.25 ? Math.min(3, Math.ceil((i - 1) / 2)) : 0;
  const phasers = t >= 0.5 ? Math.min(2, Math.ceil((i - 3) / 2)) : 0;
  const drifters = Math.max(1, total - stalkers - phasers);

  const out: EnemySpawn[] = [{ kind: EnemyKind.DRIFTER, count: drifters }];
  if (stalkers) out.push({ kind: EnemyKind.STALKER, count: stalkers });
  if (phasers) out.push({ kind: EnemyKind.PHASER, count: phasers });
  return out;
}

/**
 * What a door says about the room behind it, in one line.
 *
 * Drifters are counted but not named. Every room has them, so listing them
 * adds a word to every door and distinguishes none of them -- what a player
 * is choosing between is how many stalkers and phasers are in there.
 */
export function roomDetail(room: RoomDef): string {
  const total = room.enemies.reduce((n, e) => n + e.count, 0);
  const named = room.enemies
    .filter((e) => e.kind !== EnemyKind.DRIFTER && e.count > 0)
    .map((e) => `${e.count} ${SPECIES_NAME[e.kind]}${e.count > 1 ? 's' : ''}`);
  const mix = named.length ? ` \u00b7 ${named.join(', ')}` : '';
  return `${total} creature${total === 1 ? '' : 's'}${mix}`;
}

/** One glyph per objective, for the doors. The glyph *is* the decision. */
export const GOAL_GLYPH: Record<RoomGoalValue, string> = {
  [RoomGoal.CLEAR]: '\u2737',
  [RoomGoal.EXIT]: '\u21e5',
  [RoomGoal.SURVIVE]: '\u25f7',
  [RoomGoal.HUNT]: '\u25ce',
};

/** What each species is called where a player can read it. */
export const SPECIES_NAME: Record<EnemyKindValue, string> = {
  [EnemyKind.DRIFTER]: 'drifter',
  [EnemyKind.STALKER]: 'stalker',
  [EnemyKind.PHASER]: 'phaser',
  [EnemyKind.BROODER]: 'brooder',
};

/** One line of instruction, for the banner. A room has to say what it wants. */
export const GOAL_TEXT: Record<RoomGoalValue, string> = {
  [RoomGoal.CLEAR]: 'CLEAR THE ROOM',
  [RoomGoal.EXIT]: 'REACH THE EXIT',
  [RoomGoal.SURVIVE]: 'SURVIVE',
  [RoomGoal.HUNT]: 'KILL THE MARKED ONE',
};

/**
 * How long the room gives you, from what it is asking and how much is in it.
 *
 * A flat two minutes was sized for one to four bots that come to you, and it
 * is the wrong shape for ten creatures that do not: the last room would be the
 * only one losable to the clock, and losing to the clock means the sudden-death
 * spiral walls you in -- a bad end to a fifteen-minute run for a reason the
 * player never saw coming.
 *
 * `survive` inverts it. There the clock is the objective rather than the
 * backstop, so it is short and capped: standing off nine creatures for ninety
 * seconds is not tension, it is a chore with a countdown.
 */
export function roomSeconds(goal: RoomGoalValue, enemies: readonly EnemySpawn[]): number {
  const n = enemies.reduce((sum, e) => sum + e.count, 0);
  if (goal === RoomGoal.SURVIVE) return Math.min(75, 40 + n * 4);
  return 75 + n * 10;
}

/**
 * The objectives a room at depth `i` may ask for, hardest last.
 *
 * They arrive one at a time rather than all at once. Room one is always
 * `clear`, because it is the objective every other one is read against -- a
 * player who meets "reach the exit" before they have ever emptied a room does
 * not know what they are being spared.
 */
function goalsAt(i: number): RoomGoalValue[] {
  const pool: RoomGoalValue[] = [RoomGoal.CLEAR];
  if (i >= 1) pool.push(RoomGoal.EXIT);
  if (i >= 2) pool.push(RoomGoal.SURVIVE);
  if (i >= 3) pool.push(RoomGoal.HUNT);
  return pool;
}

/** Fisher-Yates on a copy; probes supply a seeded stream. */
function shuffled<T>(items: readonly T[], rand: () => number): T[] {
  const out = items.slice();
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

/**
 * Rolls one room at the given depth, on an arena that is not in `avoid`.
 *
 * `avoid` is a list rather than a single id because a branch has to steer
 * around every board already on the table -- the room just played and the
 * doors already dealt. Avoiding only the last one it happened to look at put
 * a door on the arena just played in 65 branches out of 200.
 */
export function rollRoom(
  i: number,
  avoid: readonly string[],
  goal?: RoomGoalValue,
  rand: () => number = Math.random,
): RoomDef {
  const playable = ARENAS.filter((a) => a.id !== 'dojo');
  const pool = playable.filter((a) => !avoid.includes(a.id));
  // Fewer arenas than the branch wants to steer around is a repeat, and a
  // repeat is better than refusing to roll a room.
  const from = pool.length ? pool : playable;
  const arena = from[Math.floor(rand() * from.length)];
  return {
    map: arena.id,
    enemies: roomEnemies(i, EXPEDITION_ROOMS),
    // The last room is always `clear`: an ending you can walk out of is not
    // one, and a boss you are allowed to ignore is scenery.
    goal: i === EXPEDITION_ROOMS - 1 ? RoomGoal.CLEAR : goal ?? shuffled(goalsAt(i), rand)[0],
    label: arena.name,
  };
}

/** True when this depth is the brooder's room -- the one that is not a choice. */
export function isFinalRoom(i: number): boolean {
  return i === EXPEDITION_ROOMS - 1;
}

/** How many doors a branch offers. Three is a decision; two is a coin. */
export const EXPEDITION_BRANCHES = 3;

/**
 * The doors on offer for room `i`.
 *
 * Objectives are dealt from a shuffled pool rather than rolled and re-rolled
 * until they differ. Rejection sampling reads as if it works and does not: with
 * three eligible goals and three doors to fill it left a duplicate in 5 runs of
 * 200, which a player would meet as two identical doors roughly every other
 * expedition. Dealing cannot produce one while the pool is large enough, and
 * when it is not -- the shallow rooms, where fewer objectives are unlocked than
 * there are doors -- a repeat is the only honest answer and the arenas carry
 * the difference instead.
 *
 * The objective is the decision being made: a player on their last hit point
 * takes the room they can walk out of over the room they have to empty.
 */
export function rollBranches(i: number, from: RoomDef, rand: () => number = Math.random): RoomDef[] {
  /*
   * Avoid repeating the room just played -- unless doing so leaves nothing to
   * choose between. At depth one only `clear` and `exit` are unlocked and the
   * room just played is always `clear`, so filtering leaves a single objective
   * and the player's first ever branch is three doors that differ by wallpaper.
   * Two objectives with one of them repeated is a decision; one is not.
   */
  const all = goalsAt(i);
  const fresh = all.filter((g) => g !== from.goal);
  const goals = shuffled(fresh.length >= 2 ? fresh : all, rand);
  const out: RoomDef[] = [];
  const used = [from.map];
  for (let n = 0; n < EXPEDITION_BRANCHES; n++) {
    const goal = goals.length ? goals[n % goals.length] : RoomGoal.CLEAR;
    // Distinct arenas too, but only as a tiebreak: two rooms with the same
    // goal on different boards still read as the same door.
    const room = rollRoom(i, used, goal, rand);
    used.push(room.map);
    out.push(room);
  }
  return out;
}

/* -------------------------------------------------------------------------- */
/* Meta-progression                                                            */
/* -------------------------------------------------------------------------- */

/** One salvage per cleared objective, with a bonus for clearing the final room. */
export function salvageFor(roomsCleared: number, finished: boolean): number {
  const cleared = Math.max(0, Math.min(EXPEDITION_ROOMS, Math.floor(roomsCleared)));
  return cleared + (finished && cleared === EXPEDITION_ROOMS ? EXPEDITION_ROOMS : 0);
}

/** Permanent unlocks add starting gear without changing room generation. */
export interface Unlock {
  code: string;
  name: string;
  blurb: string;
  cost: number;
}

export const UNLOCKS: readonly Unlock[] = [
  { code: 'bomb', name: 'Second Bomb', blurb: 'Start every run able to place two bombs.', cost: 8 },
  { code: 'fire', name: 'Longer Reach', blurb: 'Start every run with one more tile of blast.', cost: 14 },
  { code: 'kick', name: 'Steel Toes', blurb: 'Start every run able to kick bombs.', cost: 24 },
  { code: 'speed', name: 'Light Step', blurb: 'Start every run one speed level faster.', cost: 34 },
];

/**
 * The gear a player walks into room one holding, from what they have bought.
 *
 * Applied as a `PlayerGear` seed, which means it goes through the same clamps
 * the pickups respect -- an unlock cannot put anybody above `MAX_BOMBS`, and
 * the run's own drops stack on top of it normally.
 */
export function startingGear(unlocks: readonly string[]): {
  maxBombs?: number;
  power?: number;
  speedLevel?: number;
  kick?: boolean;
} {
  const has = (code: string) => unlocks.includes(code);
  return {
    maxBombs: has('bomb') ? 2 : undefined,
    power: has('fire') ? 3 : undefined,
    speedLevel: has('speed') ? 1 : undefined,
    kick: has('kick') ? true : undefined,
  };
}
